import { SelectString } from '../SelectString'

type BaseProps<T extends string> = {
  options: T[] | Readonly<T[]>
  label?: string
  'aria-label'?: string
  getOptionLabel?: (option: T) => string
  groupBy?: (option: T) => string
  error?: boolean
  helperText?: string | null
  required?: boolean
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  variant?: 'outlined' | 'standard' | 'filled'
}

interface FilterSelectStringProps<
  T extends string,
  TFilters extends Record<string, any>,
  K extends keyof TFilters,
> extends BaseProps<T> {
  filterKey: K
  value: TFilters[K]
  onFilterChange: (filter: Partial<TFilters>, submit?: boolean) => void
  multiple?: boolean
}

export function FilterSelectString<
  T extends string,
  TFilters extends Record<string, any>,
  K extends keyof TFilters,
>({
  filterKey,
  value,
  onFilterChange,
  multiple,
  ...rest
}: FilterSelectStringProps<T, TFilters, K>) {
  const handleChange = (newValue: T | T[] | null) => {
    onFilterChange({ [filterKey]: newValue } as Partial<TFilters>)
  }

  if (multiple) {
    return (
      <SelectString<T>
        {...rest}
        multiple
        value={(value as T[] | null) ?? []}
        onChange={handleChange as (value: T[]) => void}
      />
    )
  }

  return (
    <SelectString<T>
      {...rest}
      value={value as T | null}
      onChange={handleChange as (value: T | null) => void}
    />
  )
}
